/* eslint-disable no-magic-numbers */
import {
  BankTypesEnumAPI,
  BillingCurrencyEnum,
  type ContractAPI,
  type ContractorDocumentUploadResponse,
  ContractorState,
  ContractState,
  type DocumentUploadUrlResponse,
  type DownloadDocumentResponse,
  EntityType,
  type GetContractorAllowedEntitiesResponseAPI,
  type GetContractorBankDetailsResponseAPI,
  type GetContractorContractOutstandingFeesResponseAPI,
  type GetContractorIdentityVerificationResponseAPI,
  type GetContractorPersonalDetailsResponseAPI,
  type GetContractorResponseAPI,
  type GetContractorsCostsResponseAPI,
  type GetCountriesResponseAPI,
  InvoicingCurrencyEnum,
  InvoicingFrequencyEnum,
  LiabilityShieldStateAPI,
  OnboardingStepState,
  OutstandingFeeStatusEnum,
  OutstandingFeeTypeEnum,
  type UpdateContractorContractResponseAPI,
} from '@omnipresentgroup/types';

export const GET_CONTRACTORS_COSTS_RESPONSE: GetContractorsCostsResponseAPI = {
  monthly_platform_fee: '0',
  vat_rate: '0.0',
  total_cost: '0.00',
  country: {
    code: 'GB',
    name: 'United Kingdom',
    flag: '🇬🇧',
  },
};

export const GET_COUNTRIES_RESPONSE: GetCountriesResponseAPI = [
  {
    code: 'ES',
    name: 'Spain',
    flag: '🇪🇸',
  },
  {
    code: 'GB',
    name: 'United Kingdom',
    flag: '🇬🇧',
  },
  {
    code: 'US',
    name: 'United States of America',
    flag: '🇺🇸',
  },
];

export const DEFAULT_CONTRACT: ContractAPI = {
  id: 'dummy-id-0',
  contractor_id: 'b551a5a1-2641-463f-96a9-1254783a2a48',
  client_id: 'dummy-client-id-0',
  job_role: 'QA tester',
  invoicing_currency: InvoicingCurrencyEnum.EUR,
  expected_invoicing_frequency: InvoicingFrequencyEnum.PER_MONTH,
  billing_currency: BillingCurrencyEnum.EUR,
  start_date: '2023-02-15',
  end_date: '',
  rate: '1000.123',
  state: ContractState.ACTIVE,
};

export const DEFAULT_CONTRACTOR: GetContractorResponseAPI = {
  id: 'b551a5a1-2641-463f-96a9-1254783a2a48',
  first_name: 'Test',
  last_name: 'Contractor',
  email: 'e2e-contractor@omnie2etesting.com',
  residence_country: {
    code: 'GB',
    name: 'United Kingdom',
    flag: '🇬🇧',
  },
  state: ContractorState.ONBOARDING,
  contracts: [DEFAULT_CONTRACT],
  user_id: 'dummy-user-id-0',
  onboarding_states: {
    personal_state: OnboardingStepState.OPEN,
    kyc_state: OnboardingStepState.OPEN,
    banking_account_state: OnboardingStepState.OPEN,
  },
};

export const generateContractor = (
  override: Partial<
    Omit<GetContractorResponseAPI, 'contract'> & {
      contract: Partial<ContractAPI>;
    }
  > = {},
) => ({
  ...DEFAULT_CONTRACTOR,
  ...override,
  contracts: [
    {
      ...DEFAULT_CONTRACTOR.contracts[0],
      ...override.contract,
    },
  ],
});

export const generateContractorIdentityVerificationDetails = (
  override: Partial<GetContractorIdentityVerificationResponseAPI> = {},
) => ({
  ...DEFAULT_CONTRACTOR_IDENTITY_VERIFICATION,
  ...override,
});

export const CONTRACTOR_LIABILITY_SHIELD_INACTIVE = {
  state: LiabilityShieldStateAPI.INACTIVE,
  start_date: '2023-11-12',
  end_date: '2023-12-12',
  created_at: '2023-11-12T12:00:00Z',
  updated_at: '2023-11-12T12:00:00Z',
};

export const CONTRACTOR_LIABILITY_SHIELD_ACTIVE = {
  state: LiabilityShieldStateAPI.ACTIVE,
  start_date: '2023-11-12',
  created_at: '2023-11-12T12:00:00Z',
  updated_at: '2023-11-12T12:00:00Z',
};

export const CONTRACTOR_LIABILITY_SHIELD_AWAITING_ACTIVATION = {
  state: LiabilityShieldStateAPI.AWAITING_ACTIVATION,
  start_date: '2023-11-12',
  created_at: '2023-11-12T12:00:00Z',
  updated_at: '2023-11-12T12:00:00Z',
};

export const CONTRACTOR_WITH_NO_LIABILITY_SHIELD = generateContractor({
  id: 'fe9e6bbc-6011-4df1-898a-a423853021e',
  first_name: 'Elsa',
  last_name: 'Else',
  contract: {
    contractor_id: 'fe9e6bbc-6011-4df1-898a-a423853021e',
    job_role: 'Software Engineer',
    start_date: '2024-01-12',
    end_date: '2028-01-12',
  },
});

export const CONTRACTOR_WITH_ACTIVE_LIABILITY_SHIELD = generateContractor({
  id: '9c5929e2-5f36-42e5-83cd-e7ab26a8edcf',
  first_name: 'Tomas',
  last_name: 'Meyer',
  contract: {
    contractor_id: 'cf7f2227-b35f-4c4d-b568-f69c7da0b6a7',
    job_role: 'Head of Sales',
    start_date: '2024-08-12',
    end_date: '2028-09-12',
    liability_shield_coverages: [CONTRACTOR_LIABILITY_SHIELD_ACTIVE],
  },
});

export const CONTRACTOR_WITH_INACTIVE_LIABILITY_SHIELD = generateContractor({
  id: 'cbbfc84e-6d98-4c47-9ad8-ea1bab7f4c43',
  first_name: 'Teresa',
  last_name: 'Holmes',
  contract: {
    contractor_id: 'cf7f2227-b35f-4c4d-b568-f69c7da0b6a7',
    job_role: 'Principal Engineer',
    start_date: '2024-03-23',
    end_date: '2030-05-11',
    liability_shield_coverages: [CONTRACTOR_LIABILITY_SHIELD_INACTIVE],
  },
});

export const CONTRACTOR_WITH_AWAITING_ACTIVATION_LIABILITY_SHIELD =
  generateContractor({
    id: 'cbbfc84e-6d98-4c47-9ad8-ea1bab7f4c43',
    first_name: 'Alice',
    last_name: 'Sullivan',
    contract: {
      contractor_id: 'cf7f2227-b35f-4c4d-b568-f69c7da0b6a7',
      job_role: 'Principal Engineer',
      start_date: '2024-03-23',
      end_date: '2030-05-11',
      liability_shield_coverages: [
        CONTRACTOR_LIABILITY_SHIELD_AWAITING_ACTIVATION,
      ],
    },
  });

export const CONTRACTOR_WITH_ACTIVE_AND_INACTIVE_LIABILITY_SHIELD =
  generateContractor({
    id: 'b81ab5fa-ea93-4bc8-b6cf-703877da8adf',
    first_name: 'Sarah',
    last_name: 'Jones',
    contract: {
      contractor_id: 'cf7f2227-b35f-4c4d-b568-f69c7da0b6a7',
      job_role: 'Junior Engineer',
      start_date: '2024-03-23',
      end_date: '2030-05-11',
      liability_shield_coverages: [
        CONTRACTOR_LIABILITY_SHIELD_ACTIVE,
        CONTRACTOR_LIABILITY_SHIELD_INACTIVE,
      ],
    },
  });

export const CONTRACTOR_WITH_INACTIVE_LIABILITY_SHIELD_AND_HISTORY =
  generateContractor({
    id: 'b81ab5fa-ea93-4bc8-b6cf-703877da8adf',
    first_name: 'Sarah',
    last_name: 'Jones',
    contract: {
      contractor_id: 'cf7f2227-b35f-4c4d-b568-f69c7da0b6a7',
      job_role: 'Junior Engineer',
      start_date: '2024-03-23',
      end_date: '2030-05-11',
      liability_shield_coverages: [
        CONTRACTOR_LIABILITY_SHIELD_INACTIVE,
        CONTRACTOR_LIABILITY_SHIELD_INACTIVE,
      ],
    },
  });

export const DEFAULT_CONTRACTORS = [
  DEFAULT_CONTRACTOR,
  generateContractor({
    id: '86f05c68-7ab3-486b-9403-d251632f3f04',
    first_name: 'Meu',
    last_name: ' Primo',
    contract: {
      contractor_id: '86f05c68-7ab3-486b-9403-d251632f3f04',
      job_role: 'Staff Engineer',
      start_date: '2024-01-02',
    },
  }),
  generateContractor({
    id: 'd00f66dc-d616-4b8b-9a0f-6281d20fee52',
    first_name: 'That Dude',
    last_name: 'Right There',
    contract: {
      contractor_id: 'd00f66dc-d616-4b8b-9a0f-6281d20fee52',
      job_role: 'Staff Engineer',
      start_date: '2024-01-13',
    },
  }),
  generateContractor({
    id: 'fe9e6bbc-6011-4df1-898a-ad063853021e',
    first_name: 'Someone',
    last_name: 'Else',
    contract: {
      contractor_id: 'fe9e6bbc-6011-4df1-898a-ad063853021e',
      job_role: 'Senior Software Engineer',
      start_date: '2024-01-12',
    },
  }),
  generateContractor({
    id: 'fe9e6bbc-6011-4df1-898a-a423853021e',
    first_name: 'Elsa',
    last_name: 'Else',
    contract: {
      contractor_id: 'fe9e6bbc-6011-4df1-898a-a423853021e',
      job_role: 'Software Engineer',
      start_date: '2024-01-12',
      end_date: '2028-01-12',
    },
  }),
  CONTRACTOR_WITH_NO_LIABILITY_SHIELD,
  CONTRACTOR_WITH_ACTIVE_LIABILITY_SHIELD,
  CONTRACTOR_WITH_AWAITING_ACTIVATION_LIABILITY_SHIELD,
  CONTRACTOR_WITH_ACTIVE_AND_INACTIVE_LIABILITY_SHIELD,
  CONTRACTOR_WITH_INACTIVE_LIABILITY_SHIELD,
];

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_LENGTH = 1;

export const GET_CONTRACTORS_RESPONSE = {
  items: DEFAULT_CONTRACTORS,
  total: DEFAULT_CONTRACTORS.length,
  page: DEFAULT_PAGE_LENGTH,
  size: DEFAULT_PAGE_SIZE,
  pages: Math.ceil(DEFAULT_CONTRACTORS.length / DEFAULT_PAGE_SIZE),
};

export const DEFAULT_CONTRACTOR_PERSONAL_DETAILS: GetContractorPersonalDetailsResponseAPI =
  {
    state: OnboardingStepState.OPEN,
    first_name: 'Test',
    last_name: 'Contractor',
    residence_country: {
      code: 'GB',
      name: 'United Kingdom',
      flag: '🇬🇧',
    },
    nationality: {
      code: 'GB',
      name: 'United Kingdom',
      flag: '🇬🇧',
    },
    date_of_birth: '1987-01-01',
  };

export const DEFAULT_CONTRACTOR_BANK_DETAILS: GetContractorBankDetailsResponseAPI =
  {
    state: OnboardingStepState.OPEN,
    bank_account_type: BankTypesEnumAPI.IBAN,
    owner_name: 'Account Holder Name',
    iban: 'ES9121000418450200051332',
    owner_address: {
      address_line_1: 'Address Line 1',
      postal_code: '43290',
      city: 'Madrid',
      country: 'ES',
    },
  };

export const DEFAULT_CONTRACTOR_IDENTITY_VERIFICATION: GetContractorIdentityVerificationResponseAPI =
  {
    state: OnboardingStepState.OPEN,
    entity_type: EntityType.INDIVIDUAL,
    terms_agreed: false,
    entity_type_confirmed: false,
  };

export const DEFAULT_CONTRACTOR_IDENTITY_VERIFICATION_LEGAL: GetContractorIdentityVerificationResponseAPI =
  {
    state: OnboardingStepState.OPEN,
    entity_type: EntityType.BUSINESS,
    terms_agreed: false,
    entity_type_confirmed: false,
    registered_legal_name: 'Some legal name',
    entity_email: 'test@test.com',
    company_number: '12345',
    headquarters_address: {
      address_line_1: 'Baker str. 123',
      address_line_2: 'Upper side',
      city: 'London',
      postal_code: '12345',
      region: 'Somewhere',
      country: {
        code: 'GB',
        name: 'United Kingdom',
        flag: '🇬🇧',
      },
    },
    legal_representative: {
      first_name: 'Firstname',
      last_name: 'Lastname',
      date_of_birth: '1987-01-01',
      nationality: {
        code: 'GB',
        name: 'United Kingdom',
        flag: '🇬🇧',
      },
      country_of_residence: {
        code: 'GB',
        name: 'United Kingdom',
        flag: '🇬🇧',
      },
    },
  };

export const DEFAULT_DOCUMENT_URL_RESPONSE: DocumentUploadUrlResponse = {
  url: 'dummy-url',
  signature: 'dummy-signature',
  fields: {
    key: '',
  },
};

export const DEFAULT_CONTRACTOR_DOCUMENT_UPLOAD_RESPONSE: ContractorDocumentUploadResponse =
  {
    documentUrl: 'dummy-url',
    fileName: 'dummy-file-name.txt',
    signature: 'dummy-signature',
  };

export const DEFAULT_DOCUMENT_DOWNLOAD_RESPONSE: DownloadDocumentResponse = {
  url: 'dummy-url',
};

export const DEFAULT_ALLOWED_ENTITIES_RESPONSE: GetContractorAllowedEntitiesResponseAPI =
  {
    allowed_kyc_entity_types: Object.values(EntityType),
  };

export const DEFAULT_CONTRACT_OUSTANDING_FEES: GetContractorContractOutstandingFeesResponseAPI =
  [
    {
      id: 'dummy-fee-id-0',
      contract_id: 'dummy-contract-id-0',
      amount: '29.99',
      status: OutstandingFeeStatusEnum.OPEN,
      type: OutstandingFeeTypeEnum.MANAGEMENT,
      month: '2024-07-01',
      currency: BillingCurrencyEnum.GBP,
      vat_rate: '0',
    },
    {
      id: 'dummy-fee-id-1',
      contract_id: 'dummy-contract-id-0',
      amount: '10.00',
      status: OutstandingFeeStatusEnum.PAID,
      type: OutstandingFeeTypeEnum.MANAGEMENT,
      month: '2024-06-01',
      currency: BillingCurrencyEnum.GBP,
      vat_rate: '0',
    },
  ];

export const DEFAULT_CONTRACT_UPDATE_RESPONSE: UpdateContractorContractResponseAPI =
  {
    expected_invoicing_frequency: DEFAULT_CONTRACT.expected_invoicing_frequency,
    start_date: DEFAULT_CONTRACT.start_date,
    end_date: DEFAULT_CONTRACT.end_date,
    rate: DEFAULT_CONTRACT.rate,
  };
